// Montagem das abas do export do CRM.
//
// A aba "Negociações" é um catálogo de campos declarativo (CAMPOS): cada coluna
// declara grupo, rótulo, tipo, largura e como se lê o valor. O modal renderiza a
// seleção a partir daqui — acrescentar coluna é acrescentar uma entrada na lista,
// sem tocar na UI nem no writer.
//
// As demais abas são 1 linha por registro-filho (UC, nota, termo, ...), sempre
// carregando o vínculo com a negociação para dar PROCV do lado do usuário.
import { ColType, buildSheet } from '../../../../src/lib/excelExport';
import { formatDate } from '../../../../src/lib/utils';
import { modalidadeLabel } from '../../../../src/lib/modalidade';
import { getTermoValido, getStatusTermo, descreverHistoricoTermos } from '../../../../src/lib/termoValido';
import { PUBLICOS } from '../../../../src/lib/politicaComercial';
import { contaDaUc, NOME_CONTA_SICOOB } from '../../../../src/lib/contaSicoob';
import { Negociacao, Pipeline, Profile } from '../../../../types';
import { CondicaoComercial, PROCEDENCIA_LABEL, pctDesconto, resolverCondicaoComercial } from './condicaoComercial';
import { DadosExport } from './fetchExportData';

// ─────────────────────────────────────────────────────────────────────────────
// Formatação
// ─────────────────────────────────────────────────────────────────────────────

/** Excel recusa célula com mais de 32.767 caracteres — nota longa não pode
 *  derrubar o arquivo inteiro. */
const LIMITE_CELULA = 32000;

const txt = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return s.length > LIMITE_CELULA ? `${s.slice(0, LIMITE_CELULA)}… [truncado]` : s;
};

const nmr = (v: unknown): number | string => {
    if (v === null || v === undefined || v === '') return '';
    const n = Number(v);
    return Number.isFinite(n) ? n : '';
};

/** formatDate já distingue `date` (YYYY-MM-DD, sem fuso) de timestamp — usar
 *  `new Date()` cru numa coluna `date` volta um dia em UTC-3. */
const dt = (v: unknown): string => (v ? formatDate(String(v)) : '');

const dtHora = (v: unknown): string => {
    if (!v) return '';
    const d = new Date(String(v));
    if (Number.isNaN(d.getTime())) return txt(v);
    return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const simNao = (v: unknown): string => (v === true ? 'Sim' : v === false ? 'Não' : '');

/** Junta valores distintos e não vazios — usado para achatar N UCs numa célula. */
const lista = (valores: unknown[]): string =>
    Array.from(new Set(valores.map(v => txt(v).trim()).filter(Boolean))).join(', ');

const humanizar = (v: unknown): string => {
    const s = txt(v).trim();
    if (!s) return '';
    const limpo = s.replace(/_/g, ' ');
    return limpo.charAt(0).toUpperCase() + limpo.slice(1);
};

const rotular = (mapa: Record<string, string>, v: unknown): string => {
    const s = txt(v).trim();
    return s ? mapa[s] ?? humanizar(s) : '';
};

// ─────────────────────────────────────────────────────────────────────────────
// Rótulos
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_NEGOCIACAO: Record<string, string> = {
    em_andamento: 'Em andamento',
    simulacao_realizada: 'Simulação realizada',
    termo_enviado: 'Termo enviado',
    termo_assinado: 'Termo assinado',
    termo_analise: 'Termo em análise',
    termo_aprovado: 'Termo aprovado',
    termo_reprovado: 'Termo reprovado',
    ganho: 'Ganho',
    perdido: 'Perdido',
    em_pausa: 'Em pausa',
};

const STATUS_TERMO: Record<string, string> = {
    assinado: 'Assinado',
    aguardando_verificacao: 'Aguardando verificação (biometria)',
    nao_assinado: 'Não assinado',
};

const TROCA_TITULARIDADE: Record<string, string> = {
    pendente_envio: 'Pendente de envio',
    documento_enviado: 'Documento enviado',
    aguardando_reanalise: 'Aguardando reanálise',
};

const VALOR_CONTA_LUZ: Record<string, string> = {
    'menos_de_r$300': 'Menos de R$300',
    'de_r$300_a_r$700': 'De R$300 a R$700',
    'acima_de_r$700': 'Acima de R$700',
};

const STATUS_TAREFA: Record<string, string> = {
    a_iniciar: 'A iniciar',
    em_andamento: 'Em andamento',
    aguardando: 'Aguardando',
    concluido: 'Concluído',
    cancelado: 'Cancelado',
};

const TIPO_TAREFA: Record<string, string> = {
    apresentacao: 'Apresentação',
    negociacao: 'Negociação',
    pos_venda: 'Pós-venda',
    prospeccao: 'Prospecção',
    qualificacao: 'Qualificação',
    retorno: 'Retorno',
    simulacao: 'Simulação',
    visita: 'Visita',
};

const STATUS_AVALISTA: Record<string, string> = {
    cadastrado: 'Cadastrado',
    termo_enviado: 'Termo enviado',
    assinaturas_concluidas: 'Assinaturas concluídas',
    analise_reenviada: 'Análise reenviada',
    removido: 'Removido',
};

const MODALIDADE_PLANO: Record<string, string> = {
    vendedor: 'Vendedor (política com multiplicador)',
    parceiro: 'Parceiro (percentuais simples)',
};

// ─────────────────────────────────────────────────────────────────────────────
// Contexto de uma linha da aba Negociações
// ─────────────────────────────────────────────────────────────────────────────

type Registro = Record<string, any>;

export interface LinhaContexto {
    neg: Negociacao & Registro;
    /** Colunas de `negociacoes` buscadas só no export (documentos, medidor...). */
    extras: Registro;
    ucs: Registro[];
    notas: Registro[];
    analise: Registro | null;
    avalistas: Registro[];
    tarefas: Registro[];
    logs: Registro[];
    cond: CondicaoComercial;
    nomeDe: (id?: string | null) => string;
    cargoDe: (id?: string | null) => string;
    funilDe: (id?: string | null) => string;
}

export interface CampoExport {
    key: string;
    label: string;
    grupo: string;
    tipo: ColType;
    largura: number;
    /** Entra na seleção padrão ("Completo" liga tudo de qualquer forma). */
    padrao: boolean;
    /** Faz parte do recorte de fechamento pedido pelo comercial. */
    politica?: boolean;
    valor: (c: LinhaContexto) => string | number;
}

const G = {
    ident: 'Identificação',
    funil: 'Funil e status',
    contato: 'Contato',
    politica: 'Política comercial',
    consumo: 'Consumo e UCs',
    termo: 'Termo e assinatura',
    simulacao: 'Simulação',
    analise: 'Validação de dados',
    origem: 'Origem e marketing',
    local: 'Localização',
    atividade: 'Atividade e documentos',
    datas: 'Datas',
} as const;

const docs = (c: LinhaContexto): { url: string; name: string }[] => {
    const d = c.extras?.documentos_json ?? c.neg.documentos_json;
    return Array.isArray(d) ? d : [];
};

const simulacoes = (c: LinhaContexto): Registro[] =>
    Array.isArray(c.neg.url_simulacao) ? (c.neg.url_simulacao as Registro[]) : [];

const termos = (c: LinhaContexto): Registro[] => (Array.isArray(c.neg.url_termo) ? (c.neg.url_termo as Registro[]) : []);

const termoValido = (c: LinhaContexto): Registro | null => getTermoValido(termos(c) as any) as Registro | null;

const ultimaSimulacao = (c: LinhaContexto): Registro | null => {
    const todas = simulacoes(c);
    if (todas.length === 0) return null;
    return todas.reduce((maisNova, atual) =>
        Date.parse(atual?.data_criacao || '') >= Date.parse(maisNova?.data_criacao || '') ? atual : maisNova
    );
};

/** verificacao_telefonica_resultado é gravado como JSON string. */
const verifTelefonica = (analise: Registro | null): { resultado: string; observacoes: string } => {
    const bruto = analise?.verificacao_telefonica_resultado;
    if (!bruto) return { resultado: '', observacoes: '' };
    try {
        const j = typeof bruto === 'string' ? JSON.parse(bruto) : bruto;
        return { resultado: humanizar(j?.resultado_final), observacoes: txt(j?.observacoes_gerais) };
    } catch {
        return { resultado: txt(bruto), observacoes: '' };
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// Catálogo de colunas da aba Negociações
// ─────────────────────────────────────────────────────────────────────────────

export const CAMPOS: CampoExport[] = [
    // ── Identificação ────────────────────────────────────────────────────────
    { key: 'id', label: 'Nº da negociação', grupo: G.ident, tipo: 'num', largura: 14, padrao: true, politica: true, valor: c => nmr(c.neg.id) },
    { key: 'titulo', label: 'Título', grupo: G.ident, tipo: 'txt', largura: 34, padrao: true, politica: true, valor: c => txt(c.neg.titulo) },
    { key: 'cliente_nome', label: 'Cliente', grupo: G.ident, tipo: 'txt', largura: 34, padrao: true, politica: true, valor: c => txt(c.neg.cliente_nome) },
    { key: 'tipo_cliente', label: 'Tipo (PF/PJ)', grupo: G.ident, tipo: 'txt', largura: 12, padrao: true, valor: c => txt(c.neg.tipo_cliente) },
    { key: 'cpf_cnpj', label: 'CPF / CNPJ', grupo: G.ident, tipo: 'txt', largura: 20, padrao: true, politica: true, valor: c => txt(c.neg.cpf_cnpj) },
    { key: 'segmento', label: 'Segmento', grupo: G.ident, tipo: 'txt', largura: 22, padrao: true, valor: c => txt(c.neg.segmento) },
    { key: 'nome_representante_legal', label: 'Representante legal', grupo: G.ident, tipo: 'txt', largura: 30, padrao: false, valor: c => txt(c.neg.nome_representante_legal) },
    { key: 'cpf_representante', label: 'CPF do representante', grupo: G.ident, tipo: 'txt', largura: 18, padrao: false, valor: c => txt(c.neg.cpf_representante) },
    { key: 'data_nascimento_representante', label: 'Nascimento do representante', grupo: G.ident, tipo: 'txt', largura: 18, padrao: false, valor: c => dt(c.neg.data_nascimento_representante) },
    { key: 'contrato_social_data_versao', label: 'Versão do contrato social', grupo: G.ident, tipo: 'txt', largura: 20, padrao: false, valor: c => dt(c.neg.contrato_social_data_versao) },

    // ── Funil e status ───────────────────────────────────────────────────────
    { key: 'funil', label: 'Funil', grupo: G.funil, tipo: 'txt', largura: 22, padrao: true, valor: c => c.funilDe(c.neg.pipeline_id) },
    { key: 'stage', label: 'Etapa', grupo: G.funil, tipo: 'txt', largura: 22, padrao: true, politica: true, valor: c => txt(c.neg.stage) },
    { key: 'status', label: 'Status', grupo: G.funil, tipo: 'txt', largura: 20, padrao: true, politica: true, valor: c => rotular(STATUS_NEGOCIACAO, c.neg.status) },
    { key: 'responsavel', label: 'Responsável', grupo: G.funil, tipo: 'txt', largura: 26, padrao: true, politica: true, valor: c => c.nomeDe(c.neg.responsavel_id) },
    { key: 'responsavel_cargo', label: 'Cargo do responsável', grupo: G.funil, tipo: 'txt', largura: 20, padrao: true, politica: true, valor: c => c.cargoDe(c.neg.responsavel_id) },
    { key: 'avaliacao_estrelas', label: 'Qualificação (estrelas)', grupo: G.funil, tipo: 'num', largura: 14, padrao: true, valor: c => nmr(c.neg.avaliacao_estrelas) },
    { key: 'proxima_acao', label: 'Próxima ação', grupo: G.funil, tipo: 'txt', largura: 26, padrao: false, valor: c => humanizar(c.neg.proxima_acao) },
    { key: 'score_prioridade', label: 'Prioridade (P1/P2/P3)', grupo: G.funil, tipo: 'txt', largura: 14, padrao: false, valor: c => txt(c.neg.score_prioridade) },
    { key: 'favorito', label: 'Favorito', grupo: G.funil, tipo: 'txt', largura: 10, padrao: false, valor: c => simNao(c.neg.favorito) },
    { key: 'motivo_perda', label: 'Motivo de perda', grupo: G.funil, tipo: 'txt', largura: 26, padrao: false, valor: c => txt(c.neg.motivo_perda) },
    { key: 'observacoes_perda', label: 'Observações da perda', grupo: G.funil, tipo: 'txt', largura: 34, padrao: false, valor: c => txt(c.neg.observacoes_perda) },
    { key: 'tags', label: 'Tags', grupo: G.funil, tipo: 'txt', largura: 24, padrao: false, valor: c => lista(c.neg.tags || []) },

    // ── Contato ──────────────────────────────────────────────────────────────
    { key: 'whatsapp', label: 'WhatsApp', grupo: G.contato, tipo: 'txt', largura: 18, padrao: true, valor: c => txt(c.neg.whatsapp) },
    { key: 'email', label: 'E-mail', grupo: G.contato, tipo: 'txt', largura: 30, padrao: true, valor: c => txt(c.neg.email) },
    { key: 'contato_financeiro', label: 'Contato financeiro', grupo: G.contato, tipo: 'txt', largura: 24, padrao: false, valor: c => txt(c.neg.contato_financeiro) },
    { key: 'email_financeiro', label: 'E-mail financeiro', grupo: G.contato, tipo: 'txt', largura: 30, padrao: false, valor: c => txt(c.neg.email_financeiro) },
    { key: 'whatsapp_financeiro', label: 'WhatsApp financeiro', grupo: G.contato, tipo: 'txt', largura: 18, padrao: false, valor: c => txt(c.neg.whatsapp_financeiro) },

    // ── Política comercial (o recorte pedido pelo comercial) ─────────────────
    { key: 'publico', label: 'Público (A/B/C)', grupo: G.politica, tipo: 'txt', largura: 14, padrao: true, politica: true, valor: c => PUBLICOS[c.cond.publico].label },
    { key: 'publico_faixa', label: 'Faixa do público', grupo: G.politica, tipo: 'txt', largura: 22, padrao: true, politica: true, valor: c => c.cond.publicoFaixa },
    { key: 'publico_procedencia', label: 'Público — procedência', grupo: G.politica, tipo: 'txt', largura: 26, padrao: true, politica: true, valor: c => PROCEDENCIA_LABEL[c.cond.publicoProcedencia] },
    { key: 'plano_label', label: 'Plano de desconto', grupo: G.politica, tipo: 'txt', largura: 28, padrao: true, politica: true, valor: c => c.cond.planoDescricao },
    { key: 'plano_codigo', label: 'Código do plano', grupo: G.politica, tipo: 'txt', largura: 20, padrao: false, politica: true, valor: c => c.cond.plano?.codigo ?? '' },
    { key: 'plano_procedencia', label: 'Plano — procedência', grupo: G.politica, tipo: 'txt', largura: 26, padrao: true, politica: true, valor: c => PROCEDENCIA_LABEL[c.cond.planoProcedencia] },
    { key: 'plano_modalidade', label: 'Modalidade do plano', grupo: G.politica, tipo: 'txt', largura: 30, padrao: false, politica: true, valor: c => rotular(MODALIDADE_PLANO, c.cond.modalidade) },
    { key: 'plano_tabela_tarifa', label: 'Tabela: % tarifa do plano', grupo: G.politica, tipo: 'pct', largura: 18, padrao: false, politica: true, valor: c => (c.cond.plano && !c.cond.plano.foraDaPolitica ? c.cond.plano.descTarifa : '') },
    { key: 'plano_tabela_bandeira', label: 'Tabela: % bandeira do plano', grupo: G.politica, tipo: 'pct', largura: 18, padrao: false, politica: true, valor: c => (c.cond.plano && !c.cond.plano.foraDaPolitica ? c.cond.plano.descBandeira : '') },
    { key: 'desconto_tarifa', label: 'Taxa de desconto (% tarifa)', grupo: G.politica, tipo: 'pct', largura: 20, padrao: true, politica: true, valor: c => c.cond.descTarifaPct ?? '' },
    { key: 'desconto_bandeira', label: 'Desconto na bandeira (%)', grupo: G.politica, tipo: 'pct', largura: 20, padrao: true, politica: true, valor: c => c.cond.descBandeiraPct ?? '' },
    { key: 'descontos_divergentes', label: 'Descontos divergentes entre UCs', grupo: G.politica, tipo: 'txt', largura: 18, padrao: false, politica: true, valor: c => simNao(c.cond.descontosDivergentes) },
    { key: 'fidelidade', label: 'Fidelidade', grupo: G.politica, tipo: 'txt', largura: 16, padrao: true, politica: true, valor: c => c.cond.fidelidade },
    { key: 'carencia', label: 'Carência', grupo: G.politica, tipo: 'txt', largura: 22, padrao: true, politica: true, valor: c => c.cond.carencia },
    { key: 'carencia_solicitada', label: 'Carência solicitada', grupo: G.politica, tipo: 'txt', largura: 14, padrao: false, politica: true, valor: c => simNao(c.cond.carenciaSolicitada) },
    { key: 'multiplicador_plano', label: 'Multiplicador do plano', grupo: G.politica, tipo: 'kwh', largura: 18, padrao: true, politica: true, valor: c => c.cond.multPlano ?? '' },
    { key: 'fator_fidelidade', label: 'Fator de fidelidade', grupo: G.politica, tipo: 'kwh', largura: 16, padrao: true, politica: true, valor: c => c.cond.fatorFidelidade ?? '' },
    { key: 'multiplicador_final', label: 'Multiplicador final', grupo: G.politica, tipo: 'kwh', largura: 16, padrao: true, politica: true, valor: c => c.cond.multFinal ?? '' },
    { key: 'multiplicador_procedencia', label: 'Multiplicador — procedência', grupo: G.politica, tipo: 'txt', largura: 28, padrao: true, politica: true, valor: c => PROCEDENCIA_LABEL[c.cond.multProcedencia] },
    { key: 'fora_da_politica', label: 'Plano fora da política', grupo: G.politica, tipo: 'txt', largura: 16, padrao: false, politica: true, valor: c => (c.cond.plano ? simNao(c.cond.plano.foraDaPolitica) : '') },
    { key: 'plano_legado', label: 'Plano descontinuado', grupo: G.politica, tipo: 'txt', largura: 16, padrao: false, politica: true, valor: c => (c.cond.plano ? simNao(!!c.cond.plano.legado) : '') },
    { key: 'desconto_adicional', label: 'Desconto adicional', grupo: G.politica, tipo: 'txt', largura: 22, padrao: false, politica: true, valor: c => txt(c.neg.desconto_adicional) },
    { key: 'desconto_status', label: 'Status da aprovação de desconto', grupo: G.politica, tipo: 'txt', largura: 22, padrao: true, politica: true, valor: c => humanizar(c.neg.desconto_status) },

    // ── Consumo e UCs ────────────────────────────────────────────────────────
    { key: 'consumo_medio_kwh', label: 'Consumo da negociação (kWh)', grupo: G.consumo, tipo: 'kwh', largura: 20, padrao: true, politica: true, valor: c => nmr(c.neg.consumo_medio_kwh) },
    { key: 'consumo_ucs_kwh', label: 'Consumo somado das UCs (kWh)', grupo: G.consumo, tipo: 'kwh', largura: 22, padrao: true, politica: true, valor: c => c.cond.volumeUcsKwh },
    { key: 'volume_classificacao', label: 'Volume usado na classificação (kWh)', grupo: G.consumo, tipo: 'kwh', largura: 24, padrao: false, politica: true, valor: c => c.cond.volumeConsiderado },
    { key: 'qtd_ucs', label: 'Qtd. de UCs', grupo: G.consumo, tipo: 'num', largura: 12, padrao: true, politica: true, valor: c => c.ucs.length },
    { key: 'numero_uc', label: 'Nº das UCs', grupo: G.consumo, tipo: 'txt', largura: 30, padrao: true, valor: c => lista(c.ucs.length ? c.ucs.map(u => u.numero_uc) : [c.neg.numero_uc]) },
    { key: 'uc_legado', label: 'Nº UC no cadastro da negociação', grupo: G.consumo, tipo: 'txt', largura: 20, padrao: false, valor: c => txt(c.neg.numero_uc) },
    { key: 'uc_titulares', label: 'Titulares das faturas', grupo: G.consumo, tipo: 'txt', largura: 34, padrao: false, valor: c => lista(c.ucs.map(u => u.titular_fatura)) },
    { key: 'uc_cidades', label: 'Cidades das UCs', grupo: G.consumo, tipo: 'txt', largura: 26, padrao: false, valor: c => lista(c.ucs.map(u => u.cidade)) },
    { key: 'uc_concessionarias', label: 'Concessionárias', grupo: G.consumo, tipo: 'txt', largura: 22, padrao: false, valor: c => lista(c.ucs.map(u => u.concessionaria)) },
    { key: 'uc_grupos', label: 'Grupos tarifários', grupo: G.consumo, tipo: 'txt', largura: 18, padrao: false, valor: c => lista(c.ucs.map(u => u.grupo_tarifario)) },
    { key: 'uc_ligacoes', label: 'Tipos de ligação', grupo: G.consumo, tipo: 'txt', largura: 18, padrao: false, valor: c => lista(c.ucs.map(u => u.tipo_ligacao)) },
    { key: 'uc_situacoes', label: 'Situação das UCs', grupo: G.consumo, tipo: 'txt', largura: 22, padrao: false, valor: c => lista(c.ucs.map(u => u.situacao)) },
    { key: 'uc_status_financeiro', label: 'Status financeiro das UCs', grupo: G.consumo, tipo: 'txt', largura: 22, padrao: false, valor: c => lista(c.ucs.map(u => u.status_financeiro)) },
    { key: 'uc_empresa_parceira', label: 'UCs de empresa parceira', grupo: G.consumo, tipo: 'num', largura: 16, padrao: false, valor: c => c.ucs.filter(u => u.empresa_parceira_id).length },
    { key: 'uc_boleto_unico', label: 'UCs em boleto único', grupo: G.consumo, tipo: 'num', largura: 16, padrao: false, valor: c => c.ucs.filter(u => u.boleto_unico).length },

    // ── Termo e assinatura ───────────────────────────────────────────────────
    { key: 'qtd_termos', label: 'Qtd. de termos emitidos', grupo: G.termo, tipo: 'num', largura: 14, padrao: true, valor: c => termos(c).length },
    { key: 'termo_numero', label: 'Nº do termo válido', grupo: G.termo, tipo: 'txt', largura: 20, padrao: true, valor: c => txt(termoValido(c)?.numero_termo) },
    { key: 'termo_status', label: 'Status do termo válido', grupo: G.termo, tipo: 'txt', largura: 28, padrao: true, valor: c => (termos(c).length ? rotular(STATUS_TERMO, getStatusTermo(termoValido(c) as any)) : '') },
    { key: 'termo_data_criacao', label: 'Termo emitido em', grupo: G.termo, tipo: 'txt', largura: 16, padrao: false, valor: c => dt(termoValido(c)?.data_criacao) },
    { key: 'termo_data_assinatura', label: 'Termo assinado em', grupo: G.termo, tipo: 'txt', largura: 16, padrao: true, politica: true, valor: c => dt(termoValido(c)?.data_assinatura || c.neg.data_assinatura_termo) },
    { key: 'termo_sincronizado_em', label: 'Assinatura conferida em', grupo: G.termo, tipo: 'txt', largura: 18, padrao: false, valor: c => dt(termoValido(c)?.assinatura_sincronizada_em) },
    { key: 'termo_liberado_em', label: 'Assinaturas internas liberadas em', grupo: G.termo, tipo: 'txt', largura: 20, padrao: false, valor: c => dt(termoValido(c)?.assinaturas_liberadas_em) },
    { key: 'termo_procurador', label: 'Procurador', grupo: G.termo, tipo: 'txt', largura: 26, padrao: false, valor: c => txt(termoValido(c)?.signatarios?.procurador?.nome) },
    { key: 'termo_testemunha1', label: 'Testemunha 1', grupo: G.termo, tipo: 'txt', largura: 26, padrao: false, valor: c => txt(termoValido(c)?.signatarios?.testemunha1?.nome) },
    { key: 'termo_testemunha2', label: 'Testemunha 2', grupo: G.termo, tipo: 'txt', largura: 26, padrao: false, valor: c => txt(termoValido(c)?.signatarios?.testemunha2?.nome) },
    { key: 'termo_autentique', label: 'ID no Autentique', grupo: G.termo, tipo: 'txt', largura: 26, padrao: false, valor: c => txt(termoValido(c)?.id_autentique || c.extras?.id_termo_autentique) },
    { key: 'termo_url', label: 'PDF do termo válido', grupo: G.termo, tipo: 'txt', largura: 40, padrao: false, valor: c => txt(termoValido(c)?.url_pdf) },
    { key: 'termo_link_cliente', label: 'Link de assinatura do cliente', grupo: G.termo, tipo: 'txt', largura: 40, padrao: false, valor: c => txt(c.neg.termo_link_do_cliente) },
    { key: 'termo_email_recusado', label: 'E-mail do termo recusado em', grupo: G.termo, tipo: 'txt', largura: 20, padrao: false, valor: c => dt(termoValido(c)?.email_recusado_em) },

    // ── Simulação ────────────────────────────────────────────────────────────
    { key: 'qtd_simulacoes', label: 'Qtd. de simulações', grupo: G.simulacao, tipo: 'num', largura: 14, padrao: true, valor: c => simulacoes(c).length },
    { key: 'simulacao_numero', label: 'Nº da última simulação', grupo: G.simulacao, tipo: 'txt', largura: 20, padrao: false, valor: c => txt(ultimaSimulacao(c)?.numero_simulacao) },
    { key: 'simulacao_data', label: 'Última simulação em', grupo: G.simulacao, tipo: 'txt', largura: 18, padrao: false, valor: c => dt(ultimaSimulacao(c)?.data_criacao) },
    { key: 'simulacao_valor_mensal', label: 'Valor mensal simulado', grupo: G.simulacao, tipo: 'txt', largura: 20, padrao: false, valor: c => txt(ultimaSimulacao(c)?.valor_mensal) },
    { key: 'simulacao_economia', label: 'Economia simulada', grupo: G.simulacao, tipo: 'txt', largura: 20, padrao: false, valor: c => txt(ultimaSimulacao(c)?.valor_economia) },
    { key: 'simulacao_url', label: 'PDF da última simulação', grupo: G.simulacao, tipo: 'txt', largura: 40, padrao: false, valor: c => txt(ultimaSimulacao(c)?.url_pdf) },

    // ── Análise cadastral ────────────────────────────────────────────────────
    { key: 'analise_decisao', label: 'Decisão da análise', grupo: G.analise, tipo: 'txt', largura: 24, padrao: true, politica: true, valor: c => txt(c.analise?.decisao_final) },
    { key: 'analise_decisao_em', label: 'Decisão em', grupo: G.analise, tipo: 'txt', largura: 16, padrao: true, politica: true, valor: c => dt(c.analise?.decisao_final_at) },
    { key: 'analise_risco', label: 'Risco final', grupo: G.analise, tipo: 'txt', largura: 14, padrao: false, valor: c => txt(c.analise?.risco_final) },
    { key: 'analise_analista', label: 'Analista', grupo: G.analise, tipo: 'txt', largura: 24, padrao: false, valor: c => c.nomeDe(c.analise?.analista_id) },
    { key: 'analise_parecer', label: 'Parecer de crédito', grupo: G.analise, tipo: 'txt', largura: 40, padrao: false, valor: c => txt(c.analise?.parecer_credito) },
    { key: 'analise_ressalvas', label: 'Ressalvas / condições', grupo: G.analise, tipo: 'txt', largura: 40, padrao: false, valor: c => txt(c.analise?.ressalvas_condicoes) },
    { key: 'analise_score_serasa', label: 'Score Serasa', grupo: G.analise, tipo: 'txt', largura: 14, padrao: false, valor: c => txt(c.analise?.credito_score_serasa) },
    { key: 'analise_classificacao', label: 'Classificação de risco (crédito)', grupo: G.analise, tipo: 'txt', largura: 20, padrao: false, valor: c => txt(c.analise?.credito_classificacao_risco) },
    { key: 'analise_motor_score', label: 'Motor de risco — score', grupo: G.analise, tipo: 'kwh', largura: 16, padrao: false, valor: c => nmr(c.analise?.motor_risco_score) },
    { key: 'analise_motor_classe', label: 'Motor de risco — classificação', grupo: G.analise, tipo: 'txt', largura: 20, padrao: false, valor: c => txt(c.analise?.motor_risco_classificacao) },
    { key: 'analise_pendencias', label: 'Pendências financeiras', grupo: G.analise, tipo: 'txt', largura: 16, padrao: false, valor: c => simNao(c.analise?.credito_pendencias_financeiras) },
    { key: 'analise_valor_pendencias', label: 'Valor das pendências (R$)', grupo: G.analise, tipo: 'kwh', largura: 18, padrao: false, valor: c => nmr(c.analise?.credito_valor_pendencias) },
    { key: 'analise_protestos', label: 'Protestos', grupo: G.analise, tipo: 'txt', largura: 12, padrao: false, valor: c => simNao(c.analise?.credito_protestos) },
    { key: 'analise_verif_tel', label: 'Verificação telefônica', grupo: G.analise, tipo: 'txt', largura: 18, padrao: false, valor: c => verifTelefonica(c.analise).resultado },
    { key: 'analise_verif_tel_obs', label: 'Verificação telefônica — observações', grupo: G.analise, tipo: 'txt', largura: 40, padrao: false, valor: c => verifTelefonica(c.analise).observacoes },
    { key: 'analise_troca_titularidade', label: 'Necessita troca de titularidade', grupo: G.analise, tipo: 'txt', largura: 18, padrao: false, valor: c => simNao(c.analise?.necessita_troca_titularidade) },
    { key: 'troca_titularidade_status', label: 'Status da troca de titularidade', grupo: G.analise, tipo: 'txt', largura: 22, padrao: false, valor: c => rotular(TROCA_TITULARIDADE, c.neg.troca_titularidade_status) },
    { key: 'analise_iniciada_em', label: 'Análise iniciada em', grupo: G.analise, tipo: 'txt', largura: 18, padrao: false, valor: c => dtHora(c.neg.analise_cadastral_started_at) },
    { key: 'analise_minutos', label: 'Minutos de análise', grupo: G.analise, tipo: 'num', largura: 14, padrao: false, valor: c => nmr(c.neg.analise_cadastral_minutos_realizados) },
    { key: 'qtd_avalistas', label: 'Qtd. de avalistas', grupo: G.analise, tipo: 'num', largura: 12, padrao: false, valor: c => c.avalistas.length },
    { key: 'pode_reanalisar', label: 'Pode reanalisar', grupo: G.analise, tipo: 'txt', largura: 12, padrao: false, valor: c => simNao(c.extras?.pode_reanalisar) },

    // ── Origem e marketing ───────────────────────────────────────────────────
    { key: 'origem', label: 'Origem', grupo: G.origem, tipo: 'txt', largura: 24, padrao: true, valor: c => txt(c.neg.origem) },
    { key: 'plataforma', label: 'Plataforma', grupo: G.origem, tipo: 'txt', largura: 18, padrao: false, valor: c => txt(c.neg.plataforma) },
    { key: 'campanha', label: 'Campanha', grupo: G.origem, tipo: 'txt', largura: 26, padrao: false, valor: c => txt(c.neg.campanha) },
    { key: 'anuncio', label: 'Anúncio', grupo: G.origem, tipo: 'txt', largura: 26, padrao: false, valor: c => txt(c.neg.anuncio) },
    { key: 'formulario', label: 'Formulário', grupo: G.origem, tipo: 'txt', largura: 26, padrao: false, valor: c => txt(c.neg.formulario) },
    { key: 'reside_goias', label: 'Reside em Goiás', grupo: G.origem, tipo: 'txt', largura: 16, padrao: false, valor: c => simNao(c.neg.reside_goias) },
    { key: 'valor_conta_luz', label: 'Conta de luz informada', grupo: G.origem, tipo: 'txt', largura: 20, padrao: false, valor: c => rotular(VALOR_CONTA_LUZ, c.neg.valor_conta_luz) },

    // ── Localização ──────────────────────────────────────────────────────────
    { key: 'cidade', label: 'Cidade', grupo: G.local, tipo: 'txt', largura: 22, padrao: true, valor: c => txt(c.neg.cidade) },
    { key: 'bairro', label: 'Bairro', grupo: G.local, tipo: 'txt', largura: 22, padrao: false, valor: c => txt(c.neg.bairro) },
    { key: 'latitude', label: 'Latitude', grupo: G.local, tipo: 'kwh', largura: 14, padrao: false, valor: c => nmr(c.neg.latitude) },
    { key: 'longitude', label: 'Longitude', grupo: G.local, tipo: 'kwh', largura: 14, padrao: false, valor: c => nmr(c.neg.longitude) },
    { key: 'geocode_status', label: 'Status do geocode', grupo: G.local, tipo: 'txt', largura: 16, padrao: false, valor: c => humanizar(c.neg.geocode_status) },

    // ── Atividade e documentos ───────────────────────────────────────────────
    { key: 'qtd_notas', label: 'Qtd. de notas', grupo: G.atividade, tipo: 'num', largura: 12, padrao: true, valor: c => c.notas.length },
    { key: 'ultima_nota_em', label: 'Última nota em', grupo: G.atividade, tipo: 'txt', largura: 18, padrao: false, valor: c => dtHora(c.notas[c.notas.length - 1]?.created_at) },
    { key: 'ultima_nota', label: 'Última nota', grupo: G.atividade, tipo: 'txt', largura: 50, padrao: false, valor: c => txt(c.notas[c.notas.length - 1]?.conteudo) },
    { key: 'qtd_tarefas', label: 'Qtd. de tarefas/visitas', grupo: G.atividade, tipo: 'num', largura: 14, padrao: true, valor: c => c.tarefas.length },
    { key: 'tarefas_concluidas', label: 'Tarefas concluídas', grupo: G.atividade, tipo: 'num', largura: 14, padrao: false, valor: c => c.tarefas.filter(t => t.status === 'concluido').length },
    { key: 'tarefas_abertas', label: 'Tarefas em aberto', grupo: G.atividade, tipo: 'num', largura: 14, padrao: false, valor: c => c.tarefas.filter(t => t.status !== 'concluido' && t.status !== 'cancelado').length },
    { key: 'visitas_realizadas', label: 'Visitas realizadas', grupo: G.atividade, tipo: 'num', largura: 14, padrao: false, valor: c => c.tarefas.filter(t => t.tipo === 'visita' && t.status === 'concluido').length },
    { key: 'qtd_logs', label: 'Registros no histórico', grupo: G.atividade, tipo: 'num', largura: 14, padrao: false, valor: c => c.logs.length },
    { key: 'qtd_documentos', label: 'Qtd. de documentos', grupo: G.atividade, tipo: 'num', largura: 14, padrao: true, valor: c => docs(c).length },
    { key: 'documentos', label: 'Documentos anexados', grupo: G.atividade, tipo: 'txt', largura: 44, padrao: false, valor: c => lista(docs(c).map(d => d?.name)) },
    { key: 'pasta_sharepoint_url', label: 'Pasta no SharePoint', grupo: G.atividade, tipo: 'txt', largura: 40, padrao: false, valor: c => txt(c.neg.pasta_sharepoint_url) },
    { key: 'numero_medidor', label: 'Nº do medidor', grupo: G.atividade, tipo: 'txt', largura: 18, padrao: false, valor: c => txt(c.extras?.numero_medidor) },
    { key: 'medidor_capturado_em', label: 'Medidor capturado em', grupo: G.atividade, tipo: 'txt', largura: 18, padrao: false, valor: c => dtHora(c.extras?.medidor_capturado_em) },
    { key: 'medidor_capturado_por', label: 'Medidor capturado por', grupo: G.atividade, tipo: 'txt', largura: 24, padrao: false, valor: c => txt(c.extras?.medidor_capturado_por) },
    { key: 'boas_vindas_enviadas', label: 'Boas-vindas enviadas', grupo: G.atividade, tipo: 'txt', largura: 14, padrao: false, valor: c => simNao(c.extras?.boas_vindas_enviadas) },

    // ── Datas ────────────────────────────────────────────────────────────────
    { key: 'created_at', label: 'Criada em', grupo: G.datas, tipo: 'txt', largura: 18, padrao: true, politica: true, valor: c => dtHora(c.neg.created_at) },
    { key: 'updated_at', label: 'Atualizada em', grupo: G.datas, tipo: 'txt', largura: 18, padrao: true, valor: c => dtHora(c.neg.updated_at) },
    { key: 'stage_changed_at', label: 'Mudou de etapa em', grupo: G.datas, tipo: 'txt', largura: 18, padrao: false, valor: c => dtHora(c.neg.stage_changed_at) },
    { key: 'ultima_atividade_at', label: 'Última atividade em', grupo: G.datas, tipo: 'txt', largura: 18, padrao: false, valor: c => dtHora(c.neg.ultima_atividade_at) },
    { key: 'previsao_fechamento', label: 'Previsão de fechamento', grupo: G.datas, tipo: 'txt', largura: 18, padrao: false, valor: c => dt(c.neg.previsao_fechamento) },
];

export const GRUPOS: string[] = Array.from(new Set(CAMPOS.map(c => c.grupo)));

// ─────────────────────────────────────────────────────────────────────────────
// Abas
// ─────────────────────────────────────────────────────────────────────────────

export type AbaKey =
    | 'negociacoes'
    | 'ucs'
    | 'termos'
    | 'simulacoes'
    | 'notas'
    | 'analises'
    | 'avalistas'
    | 'tarefas'
    | 'logs';

export const ABAS: { key: AbaKey; nome: string; descricao: string }[] = [
    { key: 'negociacoes', nome: 'Negociações', descricao: '1 linha por negociação, com as colunas selecionadas' },
    { key: 'ucs', nome: 'Unidades Consumidoras', descricao: 'Cadastro completo de cada UC vinculada' },
    { key: 'termos', nome: 'Termos', descricao: 'Todos os termos emitidos, com status de assinatura' },
    { key: 'simulacoes', nome: 'Simulações', descricao: 'Planos de benefícios gerados' },
    { key: 'notas', nome: 'Notas', descricao: 'Feed de notas com autor e data' },
    { key: 'analises', nome: 'Validação de Dados', descricao: 'Documentos, crédito e decisão final' },
    { key: 'avalistas', nome: 'Avalistas', descricao: 'Recuperação de negociações reprovadas' },
    { key: 'tarefas', nome: 'Tarefas e Visitas', descricao: 'Agenda vinculada a cada negociação' },
    { key: 'logs', nome: 'Histórico', descricao: 'Trilha de auditoria: quem fez o quê e quando' },
];

interface Aba {
    nome: string;
    headers: string[];
    tipos: ColType[];
    larguras: number[];
    linhas: (string | number)[][];
}

const montar = (aba: Aba) => ({
    name: aba.nome,
    ws: buildSheet(aba.headers, aba.linhas, aba.tipos, { widths: aba.larguras, autofilter: true }),
});

/** Colunas de vínculo repetidas no início de toda aba-filha, para PROCV. */
const VINCULO: { label: string; tipo: ColType; largura: number }[] = [
    { label: 'Nº da negociação', tipo: 'num', largura: 14 },
    { label: 'Cliente', tipo: 'txt', largura: 32 },
    { label: 'Responsável', tipo: 'txt', largura: 24 },
];

export interface ContextoWorkbook {
    negociacoes: (Negociacao & Registro)[];
    dados: DadosExport;
    profiles: Profile[];
    pipelines: Pipeline[];
    camposSelecionados: Set<string>;
    abasSelecionadas: Set<AbaKey>;
}

export function montarAbas(ctx: ContextoWorkbook): { name: string; ws: any }[] {
    const perfilPorId = new Map(ctx.profiles.map(p => [String(p.id), p]));
    const funilPorId = new Map(ctx.pipelines.map(p => [String(p.id), p.name]));

    const nomeDe = (id?: string | null) => (id ? perfilPorId.get(String(id))?.full_name ?? '' : '');
    const cargoDe = (id?: string | null) => (id ? humanizar(perfilPorId.get(String(id))?.role) : '');
    const funilDe = (id?: string | null) => (id ? funilPorId.get(String(id)) ?? '' : '');

    const contextos: LinhaContexto[] = ctx.negociacoes.map(neg => {
        const id = Number(neg.id);
        const ucs = ctx.dados.ucs.get(id) ?? [];
        const responsavel = neg.responsavel_id ? perfilPorId.get(String(neg.responsavel_id)) : undefined;
        return {
            neg,
            extras: ctx.dados.extras.get(id) ?? {},
            ucs,
            notas: ctx.dados.notas.get(id) ?? [],
            // 1 análise por negociação; se houver linha duplicada, a mais recente decide.
            analise: (ctx.dados.analises.get(id) ?? []).slice(-1)[0] ?? null,
            avalistas: ctx.dados.avalistas.get(id) ?? [],
            tarefas: ctx.dados.tarefas.get(id) ?? [],
            logs: ctx.dados.logs.get(id) ?? [],
            cond: resolverCondicaoComercial(neg, ucs, responsavel?.role),
            nomeDe,
            cargoDe,
            funilDe,
        };
    });

    const vinculoDe = (c: LinhaContexto): (string | number)[] => [
        nmr(c.neg.id),
        txt(c.neg.cliente_nome || c.neg.titulo),
        nomeDe(c.neg.responsavel_id),
    ];

    const abas: { name: string; ws: any }[] = [];
    const quer = (k: AbaKey) => ctx.abasSelecionadas.has(k);

    // ── Negociações ──────────────────────────────────────────────────────────
    if (quer('negociacoes')) {
        const campos = CAMPOS.filter(f => ctx.camposSelecionados.has(f.key));
        if (campos.length > 0) {
            abas.push(
                montar({
                    nome: 'Negociações',
                    headers: campos.map(f => f.label),
                    tipos: campos.map(f => f.tipo),
                    larguras: campos.map(f => f.largura),
                    linhas: contextos.map(c => campos.map(f => f.valor(c))),
                })
            );
        }
    }

    // ── Unidades Consumidoras ────────────────────────────────────────────────
    if (quer('ucs')) {
        const cols: [string, ColType, number, (uc: Registro, c: LinhaContexto) => string | number][] = [
            ['Nº da UC', 'txt', 18, u => txt(u.numero_uc)],
            ['Outro nº da UC', 'txt', 18, u => txt(u.outro_numero_uc)],
            ['Nome do cliente', 'txt', 32, u => txt(u.nome_cliente)],
            ['CPF/CNPJ do cliente', 'txt', 20, u => txt(u.cpf_cnpj_cliente)],
            ['Nascimento do cliente', 'txt', 16, u => dt(u.data_nascimento_cliente)],
            ['Titular da fatura', 'txt', 32, u => txt(u.titular_fatura)],
            ['CPF/CNPJ do titular', 'txt', 20, u => txt(u.cpf_cnpj_titular)],
            ['Nascimento do titular', 'txt', 16, u => dt(u.data_nascimento_titular)],
            ['WhatsApp', 'txt', 18, u => txt(u.whatsapp_cliente)],
            ['E-mail', 'txt', 28, u => txt(u.email_cliente)],
            ['E-mail 2', 'txt', 28, u => txt(u.email_cliente_2)],
            ['Outro e-mail', 'txt', 28, u => txt(u.outro_email)],
            ['Situação', 'txt', 16, u => txt(u.situacao)],
            ['Status financeiro', 'txt', 16, u => txt(u.status_financeiro)],
            ['Consumo médio (kWh)', 'kwh', 18, u => nmr(u.consumo_medio_kwh)],
            ['Taxa de desconto (% tarifa)', 'pct', 20, u => pctDesconto(u.desconto_tarifa) ?? ''],
            ['Desconto na bandeira (%)', 'pct', 20, u => pctDesconto(u.desconto_bandeira) ?? ''],
            ['Carência', 'txt', 20, u => txt(u.carencia)],
            ['Valor da tarifa (R$)', 'kwh', 16, u => nmr(u.valor_tarifa)],
            ['Público da negociação', 'txt', 14, (_u, c) => PUBLICOS[c.cond.publico].label],
            ['Plano da negociação', 'txt', 28, (_u, c) => c.cond.planoDescricao],
            ['Fidelidade da negociação', 'txt', 16, (_u, c) => c.cond.fidelidade],
            ['Multiplicador final', 'kwh', 16, (_u, c) => c.cond.multFinal ?? ''],
            ['Concessionária', 'txt', 20, u => txt(u.concessionaria)],
            ['Grupo tarifário', 'txt', 14, u => txt(u.grupo_tarifario)],
            ['Tipo de ligação', 'txt', 16, u => txt(u.tipo_ligacao)],
            ['Modalidade GD', 'txt', 14, u => txt(u.modalidade_gd)],
            ['Nº do medidor', 'txt', 16, u => txt(u.num_medidor)],
            ['Endereço', 'txt', 40, u => txt(u.endereco)],
            ['Logradouro', 'txt', 30, u => txt(u.logradouro)],
            ['Número', 'txt', 10, u => txt(u.numero)],
            ['Complemento', 'txt', 20, u => txt(u.complemento)],
            ['Quadra', 'txt', 10, u => txt(u.quadra)],
            ['Lote', 'txt', 10, u => txt(u.lote)],
            ['Bairro', 'txt', 22, u => txt(u.bairro)],
            ['Cidade', 'txt', 22, u => txt(u.cidade)],
            ['UF', 'txt', 8, u => txt(u.estado)],
            ['CEP', 'txt', 12, u => txt(u.cep)],
            ['Segmento', 'txt', 20, u => txt(u.segmento)],
            ['Data de leitura', 'txt', 14, u => dt(u.data_leitura)],
            ['Data de apresentação', 'txt', 16, u => dt(u.data_apresentacao)],
            ['Data de vencimento', 'txt', 16, u => dt(u.data_vencimento)],
            ['Data de processamento', 'txt', 16, u => dt(u.data_processamento)],
            ['Conta Sicoob', 'txt', 18, u => NOME_CONTA_SICOOB[contaDaUc(u)]],
            ['Recebe cobrança por e-mail', 'txt', 16, u => simNao(u.receber_cobranca_email)],
            ['Boleto único', 'txt', 12, u => simNao(u.boleto_unico)],
            ['Empresa parceira (id)', 'txt', 26, u => txt(u.empresa_parceira_id)],
            ['Customer no Asaas', 'txt', 24, u => txt(u.asaas_customer_id)],
            ['Cadastro aprovado', 'txt', 14, u => simNao(u.cadastro_aprovado)],
            ['% de rateio', 'kwh', 12, u => nmr(u.percent_rateio)],
            ['Rateio travado em', 'txt', 16, u => dt(u.rateio_travado_em)],
            ['Motivo do travamento', 'txt', 18, u => humanizar(u.rateio_motivo_travamento)],
            ['Vinculada à UG em', 'txt', 16, u => dt(u.rateio_vinculado_em)],
            ['Banco de créditos', 'txt', 14, u => simNao(u.rateio_e_banco_creditos)],
            ['Observações da UC', 'txt', 40, u => txt(u.observacoes_uc)],
            ['Cadastrada em', 'txt', 16, u => dt(u.created_at)],
        ];
        const linhas = contextos.flatMap(c => c.ucs.map(u => [...vinculoDe(c), ...cols.map(([, , , get]) => get(u, c))]));
        abas.push(
            montar({
                nome: 'Unidades Consumidoras',
                headers: [...VINCULO.map(v => v.label), ...cols.map(([h]) => h)],
                tipos: [...VINCULO.map(v => v.tipo), ...cols.map(([, t]) => t)],
                larguras: [...VINCULO.map(v => v.largura), ...cols.map(([, , w]) => w)],
                linhas,
            })
        );
    }

    // ── Termos ───────────────────────────────────────────────────────────────
    if (quer('termos')) {
        const headers = [
            'Ordem de emissão', 'Nº do termo', 'Tipo de cliente', 'Status', 'É o termo válido',
            'Emitido em', 'Assinado em', 'Assinatura verificada', 'Conferido em', 'Assinaturas internas liberadas em',
            'Procurador', 'Testemunha 1', 'Testemunha 2', 'Simulação anexada', 'ID no Autentique',
            'E-mail entregue em', 'E-mail recusado em', 'Motivo da recusa', 'PDF do termo',
        ];
        const tipos: ColType[] = ['num', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt'];
        const larguras = [14, 20, 14, 28, 14, 16, 16, 18, 16, 22, 26, 26, 26, 20, 26, 18, 18, 34, 40];
        const linhas = contextos.flatMap(c =>
            descreverHistoricoTermos(termos(c) as any).map(item => {
                const t = item.termo as Registro;
                return [
                    ...vinculoDe(c),
                    item.ordemEmissao,
                    txt(t.numero_termo),
                    txt(t.tipo_cliente),
                    rotular(STATUS_TERMO, item.status),
                    simNao(item.ehTermoValido),
                    dt(t.data_criacao),
                    dt(t.data_assinatura),
                    t.assinatura_verificada === undefined ? '' : simNao(t.assinatura_verificada),
                    dt(t.assinatura_sincronizada_em),
                    dt(t.assinaturas_liberadas_em),
                    txt(t.signatarios?.procurador?.nome),
                    txt(t.signatarios?.testemunha1?.nome),
                    txt(t.signatarios?.testemunha2?.nome),
                    txt(t.numero_simulacao),
                    txt(t.id_autentique),
                    dt(t.email_entregue_em),
                    dt(t.email_recusado_em),
                    txt(t.email_recusa_motivo),
                    txt(t.url_pdf),
                ];
            })
        );
        abas.push(
            montar({
                nome: 'Termos',
                headers: [...VINCULO.map(v => v.label), ...headers],
                tipos: [...VINCULO.map(v => v.tipo), ...tipos],
                larguras: [...VINCULO.map(v => v.largura), ...larguras],
                linhas,
            })
        );
    }

    // ── Simulações ───────────────────────────────────────────────────────────
    if (quer('simulacoes')) {
        const headers = ['Nº da simulação', 'Gerada em', 'Valor mensal', 'Economia', 'PDF da simulação'];
        const tipos: ColType[] = ['txt', 'txt', 'txt', 'txt', 'txt'];
        const larguras = [20, 16, 20, 20, 40];
        const linhas = contextos.flatMap(c =>
            simulacoes(c).map(s => [
                ...vinculoDe(c),
                txt(s.numero_simulacao),
                dt(s.data_criacao),
                txt(s.valor_mensal),
                txt(s.valor_economia),
                txt(s.url_pdf),
            ])
        );
        abas.push(
            montar({
                nome: 'Simulações',
                headers: [...VINCULO.map(v => v.label), ...headers],
                tipos: [...VINCULO.map(v => v.tipo), ...tipos],
                larguras: [...VINCULO.map(v => v.largura), ...larguras],
                linhas,
            })
        );
    }

    // ── Notas ────────────────────────────────────────────────────────────────
    if (quer('notas')) {
        const headers = ['Data', 'Autor', 'Tipo', 'Conteúdo', 'Transcrição', 'Anexo', 'URL do anexo'];
        const tipos: ColType[] = ['txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt'];
        const larguras = [18, 26, 12, 70, 50, 26, 40];
        const linhas = contextos.flatMap(c =>
            c.notas.map(n => [
                ...vinculoDe(c),
                dtHora(n.created_at),
                nomeDe(n.user_id),
                humanizar(n.tipo),
                txt(n.conteudo),
                txt(n.transcricao),
                txt(n.arquivo_nome),
                txt(n.arquivo_url),
            ])
        );
        abas.push(
            montar({
                nome: 'Notas',
                headers: [...VINCULO.map(v => v.label), ...headers],
                tipos: [...VINCULO.map(v => v.tipo), ...tipos],
                larguras: [...VINCULO.map(v => v.largura), ...larguras],
                linhas,
            })
        );
    }

    // ── Análise Cadastral ────────────────────────────────────────────────────
    if (quer('analises')) {
        const cols: [string, ColType, number, (a: Registro) => string | number][] = [
            ['Decisão final', 'txt', 24, a => txt(a.decisao_final)],
            ['Decisão em', 'txt', 16, a => dtHora(a.decisao_final_at)],
            ['Risco final', 'txt', 14, a => txt(a.risco_final)],
            ['Analista', 'txt', 24, a => nomeDe(a.analista_id)],
            ['Etapa atual', 'num', 12, a => nmr(a.step_atual)],
            ['Doc. identificação', 'txt', 14, a => simNao(a.docs_identificacao)],
            ['Doc. contrato social', 'txt', 14, a => simNao(a.docs_contrato_social)],
            ['Doc. faturamento', 'txt', 14, a => simNao(a.docs_faturamento)],
            ['Doc. faturas de energia', 'txt', 14, a => simNao(a.docs_faturas_energia)],
            ['Doc. procuração', 'txt', 14, a => simNao(a.docs_procuracao)],
            ['Termo de adesão assinado', 'txt', 20, a => txt(a.termo_adesao_assinado)],
            ['Pendências nos documentos', 'txt', 40, a => txt(a.pendencias_obs_docs)],
            ['É condomínio', 'txt', 12, a => simNao(a.is_condominio)],
            ['Pendências financeiras', 'txt', 14, a => simNao(a.credito_pendencias_financeiras)],
            ['Valor das pendências (R$)', 'kwh', 18, a => nmr(a.credito_valor_pendencias)],
            ['Protestos', 'txt', 12, a => simNao(a.credito_protestos)],
            ['Valor dos protestos (R$)', 'kwh', 18, a => nmr(a.credito_valor_protestos)],
            ['Cheques sem fundo', 'txt', 14, a => simNao(a.credito_cheques_fundo)],
            ['Valor dos cheques (R$)', 'kwh', 18, a => nmr(a.credito_valor_cheques)],
            ['Score Serasa', 'txt', 14, a => txt(a.credito_score_serasa)],
            ['Risco sugerido', 'txt', 16, a => txt(a.credito_risco_sugerido)],
            ['Classificação de risco', 'txt', 18, a => txt(a.credito_classificacao_risco)],
            ['Classificação bom pagador', 'txt', 20, a => txt(a.credito_classificacao_bom_pagador)],
            ['Motor de risco — score', 'kwh', 16, a => nmr(a.motor_risco_score)],
            ['Motor de risco — classificação', 'txt', 20, a => txt(a.motor_risco_classificacao)],
            ['Consumo compensável (kWh)', 'kwh', 20, a => nmr(a.consumo_compensavel_kwh)],
            ['Parecer de crédito', 'txt', 50, a => txt(a.parecer_credito)],
            ['Ressalvas / condições', 'txt', 50, a => txt(a.ressalvas_condicoes)],
            ['Verificação telefônica', 'txt', 18, a => verifTelefonica(a).resultado],
            ['Verificação telefônica — observações', 'txt', 50, a => verifTelefonica(a).observacoes],
            ['Verificação feita por', 'txt', 24, a => nomeDe(a.verificacao_telefonica_user_id)],
            ['Verificação em', 'txt', 16, a => dtHora(a.verificacao_telefonica_at)],
            ['Necessita troca de titularidade', 'txt', 18, a => simNao(a.necessita_troca_titularidade)],
            ['Aprovação liberada por', 'txt', 24, a => nomeDe(a.aprovacao_liberada_por)],
            ['Aprovação liberada em', 'txt', 16, a => dtHora(a.aprovacao_liberada_em)],
            ['Justificativa da liberação', 'txt', 40, a => txt(a.aprovacao_liberada_justificativa)],
            ['Relatório Serasa (URL)', 'txt', 40, a => txt(a.credito_relatorio_serasa_url)],
            ['Histórico de pagamento (URL)', 'txt', 40, a => txt(a.credito_historico_pagamento_url)],
            ['Criada em', 'txt', 16, a => dtHora(a.created_at)],
            ['Atualizada em', 'txt', 16, a => dtHora(a.updated_at)],
        ];
        const linhas = contextos.flatMap(c =>
            (ctx.dados.analises.get(Number(c.neg.id)) ?? []).map(a => [...vinculoDe(c), ...cols.map(([, , , get]) => get(a))])
        );
        abas.push(
            montar({
                nome: 'Validação de Dados',
                headers: [...VINCULO.map(v => v.label), ...cols.map(([h]) => h)],
                tipos: [...VINCULO.map(v => v.tipo), ...cols.map(([, t]) => t)],
                larguras: [...VINCULO.map(v => v.largura), ...cols.map(([, , w]) => w)],
                linhas,
            })
        );
    }

    // ── Avalistas ────────────────────────────────────────────────────────────
    if (quer('avalistas')) {
        const headers = [
            'Nome do avalista', 'CPF', 'E-mail', 'Telefone', 'Status', 'Titular assinou em',
            'Avalista assinou em', 'Assinatura pendente (biometria) em', 'Biometria reprovada em',
            'ID no Autentique', 'PDF do termo', 'Link do titular', 'Link do avalista', 'Cadastrado em',
        ];
        const tipos: ColType[] = ['txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt'];
        const larguras = [30, 18, 28, 18, 22, 16, 16, 20, 18, 26, 40, 40, 40, 16];
        const linhas = contextos.flatMap(c =>
            c.avalistas.map(a => [
                ...vinculoDe(c),
                txt(a.nome_completo),
                txt(a.cpf),
                txt(a.email),
                txt(a.telefone),
                rotular(STATUS_AVALISTA, a.status),
                dt(a.titular_assinou_em),
                dt(a.avalista_assinou_em),
                dt(a.avalista_assinatura_pendente_em),
                dt(a.avalista_biometria_reprovada_em),
                txt(a.id_termo_autentique),
                txt(a.termo_url_pdf),
                txt(a.link_titular),
                txt(a.link_avalista),
                dtHora(a.created_at),
            ])
        );
        abas.push(
            montar({
                nome: 'Avalistas',
                headers: [...VINCULO.map(v => v.label), ...headers],
                tipos: [...VINCULO.map(v => v.tipo), ...tipos],
                larguras: [...VINCULO.map(v => v.largura), ...larguras],
                linhas,
            })
        );
    }

    // ── Tarefas e Visitas ────────────────────────────────────────────────────
    if (quer('tarefas')) {
        const headers = [
            'Título', 'Tipo', 'Categoria', 'Modalidade', 'Objetivo', 'Status', 'Prioridade',
            'Responsável', 'Solicitante', 'Início', 'Vencimento', 'Hora da visita', 'Concluída em',
            'Resultado', 'Origem da atribuição', 'Motivo da sugestão', 'Descrição', 'Criada em',
        ];
        const tipos: ColType[] = ['txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt'];
        const larguras = [34, 16, 18, 14, 22, 16, 14, 24, 24, 14, 14, 12, 16, 24, 20, 30, 50, 16];
        const linhas = contextos.flatMap(c =>
            c.tarefas.map(t => [
                ...vinculoDe(c),
                txt(t.titulo),
                rotular(TIPO_TAREFA, t.tipo),
                humanizar(t.categoria),
                t.modalidade ? modalidadeLabel(t.modalidade) : '',
                humanizar(t.objetivo),
                rotular(STATUS_TAREFA, t.status),
                humanizar(t.prioridade),
                nomeDe(t.responsavel_id),
                nomeDe(t.solicitante_id),
                dt(t.data_inicio),
                dt(t.vencimento),
                txt(t.hora_visita),
                dtHora(t.data_conclusao),
                lista(Array.isArray(t.resultado_categoria) ? t.resultado_categoria.map(humanizar) : []),
                humanizar(t.origem_atribuicao),
                txt(t.motivo_sugestao),
                txt(t.descricao),
                dtHora(t.created_at),
            ])
        );
        abas.push(
            montar({
                nome: 'Tarefas e Visitas',
                headers: [...VINCULO.map(v => v.label), ...headers],
                tipos: [...VINCULO.map(v => v.tipo), ...tipos],
                larguras: [...VINCULO.map(v => v.largura), ...larguras],
                linhas,
            })
        );
    }

    // ── Histórico (trilha de auditoria) ──────────────────────────────────────
    if (quer('logs')) {
        const headers = ['Data', 'Autor', 'Ação', 'Detalhes'];
        const tipos: ColType[] = ['txt', 'txt', 'txt', 'txt'];
        const larguras = [18, 26, 30, 80];
        const linhas = contextos.flatMap(c =>
            c.logs.map(l => [...vinculoDe(c), dtHora(l.created_at), nomeDe(l.user_id), humanizar(l.acao), txt(l.detalhes)])
        );
        abas.push(
            montar({
                nome: 'Histórico',
                headers: [...VINCULO.map(v => v.label), ...headers],
                tipos: [...VINCULO.map(v => v.tipo), ...tipos],
                larguras: [...VINCULO.map(v => v.largura), ...larguras],
                linhas,
            })
        );
    }

    return abas;
}
